import { type IntlShape } from 'react-intl-next';

import {
	type ConfluenceSearchPageResult,
	type ConfluenceSearchResultWithLinks,
	searchConfluencePage,
	searchConfluencePages,
} from '../../../../services/fetch-page';
import {
	type ConfluenceSearchSpaceResult,
	type CrossProductSearchSpaceResult,
	searchConfluenceSpace,
	searchConfluenceSpaces,
} from '../../../../services/fetch-space';
import { removeTrailingSlash } from '../../../../services/utils';

import { messages } from './messages';

export type PageSearchResult = Pick<ConfluenceSearchResultWithLinks, 'results' | '_links'>;

export type SpaceOption = {
	label: string;
	value: {
		spaceId: string;
		spaceKey: string;
		spaceName: string;
		spaceUrl: string;
		iconPath: string;
		isPage: false;
	};
};

export type PageOption = {
	label: string;
	value: {
		spaceId: string;
		spaceUrl: string;
		spaceKey: string;
		spaceName: string;
		linkedPageId: string;
		linkedPageTitle: string;
		linkedPageUrl: string;
		isPage: true;
		isLive?: boolean;
	};
};

export type MergedOptions = [
	{
		label: string;
		options: SpaceOption[];
	},
	{
		label: string;
		options: PageOption[];
	},
];

const mapPageAndSpaceOptions = (
	spaceSearch: CrossProductSearchSpaceResult,
	pageSearch: PageSearchResult,
	intl: IntlShape,
): MergedOptions => {
	const spaceOptions: SpaceOption[] = spaceSearch?.scopes?.[0]?.results?.map(
		({ title, space, baseUrl, url, id }) => ({
			label: title,
			value: {
				spaceId: id,
				spaceKey: space?.key,
				spaceName: title,
				spaceUrl: `${baseUrl}${url}`,
				iconPath: `${baseUrl}${space?.icon?.path}`,
				isPage: false,
			},
		}),
	);

	const pageOptions: PageOption[] = pageSearch?.results?.map(
		({ title, space, id, _links, subType }) => ({
			label: title,
			value: {
				spaceId: `${space.id}`,
				spaceKey: space.key,
				spaceName: space.name,
				linkedPageId: id,
				linkedPageTitle: title,
				isPage: true,
				isLive: subType === 'live',
				spaceUrl: `${pageSearch._links.base}/spaces/${space.key}`,
				linkedPageUrl: `${pageSearch._links.base}${_links?.webui}`,
			},
		}),
	);

	return [
		{
			label: intl.formatMessage(messages.spacesOptionLabel),
			options: spaceOptions,
		},
		{
			label: intl.formatMessage(messages.contentOptionLabel),
			options: pageOptions,
		},
	];
};

const SPACES_MAX_RESULTS = 5;

export const fetchPageAndSpaceOptions = async (
	inputValue: string,
	cloudId: string,
	gatewayBaseUrl: string = '',
	intl: IntlShape,
) => {
	const pageSearch: PageSearchResult = await searchConfluencePages({
		searchString: inputValue,
		cloudId,
		gatewayBaseUrl,
	});

	let spaceSearch: CrossProductSearchSpaceResult = await searchConfluenceSpaces({
		cloudId,
		limit: SPACES_MAX_RESULTS, // Limit displayed spaces to give room for pages results
		searchString: inputValue,
		gatewayBaseUrl,
	});
	const options = mapPageAndSpaceOptions(spaceSearch, pageSearch, intl);

	return options;
};

export const mapDefaultValues = (
	intl: IntlShape,
	spaceSearch?: ConfluenceSearchSpaceResult,
	pageSearch?: ConfluenceSearchPageResult,
	gatewayBaseUrl?: string,
) => {
	const spaceOption: SpaceOption | undefined = spaceSearch && {
		label: spaceSearch.name,
		value: {
			isPage: false,
			spaceId: spaceSearch.id,
			spaceKey: spaceSearch.key,
			spaceName: spaceSearch.name,
			iconPath: `${removeTrailingSlash(gatewayBaseUrl)}${spaceSearch.icon?.path}`,
			spaceUrl: `${removeTrailingSlash(gatewayBaseUrl)}${spaceSearch._links?.webui}`,
		},
	};

	const isPageAndSpaceRelated = pageSearch?.spaceId === spaceSearch?.id;

	const pageOption: PageOption | undefined = pageSearch && {
		label: pageSearch.title || intl.formatMessage(messages.pageUntitledDraft),
		value: {
			isPage: true,
			isLive: pageSearch.subtype === 'live',
			spaceId: pageSearch.spaceId,
			spaceKey: isPageAndSpaceRelated && spaceSearch ? spaceSearch.key : '',
			spaceName: isPageAndSpaceRelated && spaceSearch ? spaceSearch.name : '',
			linkedPageId: pageSearch.id,
			linkedPageTitle: pageSearch.title,
			spaceUrl:
				isPageAndSpaceRelated && spaceSearch
					? `${removeTrailingSlash(gatewayBaseUrl)}${spaceSearch?._links?.webui}`
					: '',
			linkedPageUrl: '',
		},
	};

	/**
	 * returns pageOption first as it has a higher specificity than spaceOption
	 */
	return pageOption ?? spaceOption;
};

export const fetchDefaultValues = async (
	intl: IntlShape,
	cloudId: string,
	gatewayBaseUrl: string = '',
	defaultSpaceId?: string,
	defaultParentId?: string,
	onFailure?: (error: unknown) => void,
) => {
	let pageSearch;
	let spaceSearch;

	if (defaultSpaceId) {
		spaceSearch = searchConfluenceSpace({
			cloudId,
			gatewayBaseUrl,
			defaultSpaceId,
		});
	}

	if (defaultParentId) {
		pageSearch = searchConfluencePage({
			cloudId,
			gatewayBaseUrl,
			defaultParentId,
		});
	}

	const responses = await Promise.allSettled([spaceSearch, pageSearch]);

	responses.forEach((res) => {
		if (res.status === 'rejected') {
			onFailure?.(res.reason);
		}
	});

	const [page, space] = responses;

	return mapDefaultValues(
		intl,
		page.status === 'fulfilled' ? page.value : undefined,
		space.status === 'fulfilled' ? space.value : undefined,
		gatewayBaseUrl,
	);
};
